import { compactToolCallOutput } from './toolOutput';

export const USAGE_TOOL_NAME = 'Usage';

/** A turn's token counts, summed over its model calls. */
export type UsageTokens = {
  input: number;
  cacheRead: number;
  cacheWrite: number;
  output: number;
};

export type UsageChipDisplay = {
  tokenChips: string[];
  sessionId?: string;
  routeName?: string;
  /** Shown as a line under the row (and under the Context Map), never as raw JSON. */
  tokens?: UsageTokens;
};

type UsageToolOutput = {
  toolName: typeof USAGE_TOOL_NAME;
  toolUseId: string;
  output: unknown;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readString = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const parseOutput = (output: unknown): unknown => {
  if (typeof output !== 'string') return output;
  const trimmed = output.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return output;
  try {
    return JSON.parse(trimmed);
  } catch {
    return output;
  }
};

const tokenChipsFrom = (record: Record<string, unknown> | null): string[] => {
  if (!record) return [];

  const input =
    record.input_tokens ?? record.prompt_tokens ?? record.inputTokens;
  const output =
    record.output_tokens ?? record.completion_tokens ?? record.outputTokens;
  const parts: string[] = [];

  if (typeof input === 'number' && Number.isFinite(input)) {
    parts.push(`${input.toLocaleString()} in`);
  }
  if (typeof output === 'number' && Number.isFinite(output)) {
    parts.push(`${output.toLocaleString()} out`);
  }

  return parts;
};

/**
 * Read a done-event route object. A missing route, a string, or an empty
 * object yields null so the chip does not invent one.
 */
export const readAgentStreamRoute = (
  value: unknown
): {
  name?: string;
  model?: string;
  finalModel?: string;
  escalated: boolean;
} | null => {
  const record = asRecord(value);
  if (!record) return null;

  const name = readString(record.name);
  const model = readString(record.model);
  const finalModel = readString(record.finalModel);
  const escalated = record.escalated === true;

  if (!name && !model && !finalModel && !escalated) return null;

  return {
    ...(name ? { name } : {}),
    ...(model ? { model } : {}),
    ...(finalModel ? { finalModel } : {}),
    escalated,
  };
};

const firstCount = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, value);
    }
  }
  return null;
};

/**
 * Input, cache read, cache write and output tokens from a done event's usage, in
 * FSI's camelCase or the API's snake_case. Missing cache counts read 0, so the
 * caching story always shows; no input or output count means no line.
 */
export const readUsageTokens = (usage: unknown): UsageTokens | null => {
  const record = asRecord(usage);
  if (!record) return null;
  const input = firstCount(record, [
    'inputTokens',
    'input_tokens',
    'prompt_tokens',
  ]);
  const output = firstCount(record, [
    'outputTokens',
    'output_tokens',
    'completion_tokens',
  ]);
  if (input === null && output === null) return null;
  return {
    input: input ?? 0,
    cacheRead:
      firstCount(record, ['cacheReadTokens', 'cache_read_input_tokens']) ?? 0,
    cacheWrite:
      firstCount(record, [
        'cacheCreationTokens',
        'cache_creation_input_tokens',
      ]) ?? 0,
    output: output ?? 0,
  };
};

export const readUsageChipDisplay = (
  output: unknown
): UsageChipDisplay | null => {
  const record = asRecord(parseOutput(output));
  if (!record) return null;

  const hasUsageField = 'usage' in record;
  const legacy =
    !hasUsageField && !('sessionId' in record) && !('route' in record);
  const usageValue = hasUsageField ? record.usage : legacy ? record : undefined;
  const tokenChips = tokenChipsFrom(asRecord(usageValue));
  const tokens = readUsageTokens(usageValue);
  const sessionId = readString(record.sessionId);
  const routeName = readAgentStreamRoute(record.route)?.name;

  if (!tokenChips.length && !sessionId && !routeName) {
    return null;
  }

  return {
    tokenChips,
    ...(sessionId ? { sessionId } : {}),
    ...(routeName ? { routeName } : {}),
    ...(tokens ? { tokens } : {}),
  };
};

/**
 * Usage row for a stream `done` event. Session id and route are chip metadata.
 * Callers keep them off the assistant message body.
 */
export const usageToolCallOutput = (input: {
  messageId: string;
  usage?: unknown;
  sessionId?: string;
  route?: unknown;
}): UsageToolOutput | null => {
  const hasUsage = input.usage !== undefined && input.usage !== null;
  const sessionId = readString(input.sessionId);
  const route = readAgentStreamRoute(input.route);

  if (!hasUsage && !sessionId) return null;

  const output: Record<string, unknown> = {};
  if (hasUsage) output.usage = input.usage;
  if (sessionId) output.sessionId = sessionId;
  if (route) {
    output.route = {
      ...(route.name ? { name: route.name } : {}),
      ...(route.model ? { model: route.model } : {}),
      ...(route.finalModel ? { finalModel: route.finalModel } : {}),
      ...(route.escalated ? { escalated: true } : {}),
    };
  }

  const compacted = compactToolCallOutput({
    toolName: USAGE_TOOL_NAME,
    toolUseId: `${input.messageId}-usage`,
    output,
  });

  return {
    toolName: USAGE_TOOL_NAME,
    toolUseId: compacted.toolUseId,
    output: compacted.output,
  };
};
